import React, { useEffect, useState, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';

interface SubPageDataContext {
  nosotrosData: RadioSectionProps
}

interface ImagesProps {
  head: string;
  line: string;
  plane: string;
  doll: string;
  parasuit: string;
  frame: string;
  bee: string;
  main: string;
  mision: string;
  vision: string;
}

interface RadioSectionProps {
  title: string;
  subtitle: string;
  description?: string;
  images: ImagesProps;
}

interface RadioStats {
  listeners_count: number;
  is_streaming: boolean;
  is_rtmp_connected?: boolean;
  stream_info?: {
    station_name?: string;
    bitrate?: string;
  };
  uptime_seconds?: number;
}

const KidsaRadio = () => {
  const { nosotrosData } = useOutletContext<SubPageDataContext>();
  const { title, images } = nosotrosData;

  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [volume, setVolume] = useState(70);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [bufferEnd, setBufferEnd] = useState(0);
  const [isLive, setIsLive] = useState(true);
  const [stats, setStats] = useState<RadioStats>({
    listeners_count: 0,
    is_streaming: false,
    is_rtmp_connected: false
  });
  const [connectionError, setConnectionError] = useState(false);
  const [myListenerId, setMyListenerId] = useState<string | null>(null);

  const audioRef = useRef<HTMLAudioElement>(null);
  const statsIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeUpdateIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // FIXED: Usar la URL correcta del servidor
  const SERVER_URL = 'http://98.201.126.138:8888';
  const WS_URL = 'ws://98.201.126.138:8888/ws';

  const togglePlay = async () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      stopRadio();
    } else {
      playRadio();
    }
  };

  const playRadio = async () => {
    if (!audioRef.current) return;

    try {
      setIsLoading(true);
      setConnectionError(false);

      // FIXED: Mejorar la URL del stream con parámetros correctos
      const timestamp = Date.now();
      const streamUrl = `${SERVER_URL}/stream?t=${timestamp}&nocache=${Math.random()}`;
      
      console.log('🎵 Conectando a:', streamUrl);
      
      audioRef.current.src = streamUrl;
      audioRef.current.load();

      const handleLoadedMetadata = () => {
        if (audioRef.current) {
          forceGoToLive();
        }
      };

      audioRef.current.addEventListener('loadedmetadata', handleLoadedMetadata, { once: true });

      await audioRef.current.play();
      setIsPlaying(true);
      setIsLoading(false);
      setIsLive(true);
      startStatsUpdates();
      startTimeTracking();
      addListener();
      
      console.log('✅ Reproducción iniciada');
    } catch (error) {
      console.error('❌ Error reproduciendo:', error);
      setIsLoading(false);
      setConnectionError(true);
    }
  };

  const stopRadio = () => {
    if (!audioRef.current) return;

    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    setIsPlaying(false);
    setIsLoading(false);
    setCurrentTime(0);
    setBufferEnd(0);
    setIsLive(true);
    stopStatsUpdates();
    stopTimeTracking();
    removeListener();
    setConnectionError(false);
    
    console.log('⏹️ Reproducción detenida');
  };

  const handleVolumeChange = (newVolume: number) => {
    if (!audioRef.current) return;
    setVolume(newVolume);
    audioRef.current.volume = newVolume / 100;
    
    // Guardar volumen en localStorage
    localStorage.setItem('radioVolume', newVolume.toString());
  };

  const handleTimeChange = (newTime: number) => {
    if (!audioRef.current || isLive) return;
    setCurrentTime(newTime);
    audioRef.current.currentTime = newTime;
    
    checkIfLive();
  };

  const forceGoToLive = () => {
    if (!audioRef.current) return;
    
    try {
      if (audioRef.current.seekable && audioRef.current.seekable.length > 0) {
        const endTime = audioRef.current.seekable.end(0);
        audioRef.current.currentTime = endTime;
        setCurrentTime(endTime);
        setIsLive(true);
      } else if (audioRef.current.duration && audioRef.current.duration > 0) {
        audioRef.current.currentTime = audioRef.current.duration;
        setCurrentTime(audioRef.current.duration);
        setIsLive(true);
      }
    } catch (error) {
      console.error('Error al ir al vivo:', error);
    }
  };

  const goToLive = () => {
    forceGoToLive();
  };

  const fastForward = () => {
    if (!audioRef.current) return;
    
    const current = audioRef.current.currentTime;
    const buffer = bufferEnd;
    
    if (buffer > current) {
      const newTime = Math.max(current + 30, buffer * 0.9);
      audioRef.current.currentTime = Math.min(newTime, buffer);
      setCurrentTime(audioRef.current.currentTime);
      checkIfLive();
    }
  };

  const checkIfLive = () => {
    if (!audioRef.current) return;
    
    const current = audioRef.current.currentTime;
    const tolerance = 15; 
    
    if (audioRef.current.seekable && audioRef.current.seekable.length > 0) {
      const end = audioRef.current.seekable.end(0);
      setBufferEnd(end);
      const isNearEnd = Math.abs(end - current) < tolerance;
      setIsLive(isNearEnd);
    } else if (audioRef.current.duration && audioRef.current.duration > 0) {
      const end = audioRef.current.duration;
      setBufferEnd(end);
      const isNearEnd = Math.abs(end - current) < tolerance;
      setIsLive(isNearEnd);
    }
  };

  const formatTime = (seconds: number): string => {
    if (!isFinite(seconds) || isNaN(seconds)) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // FIXED: Mejorar la función updateStats
  const updateStats = async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 segundos timeout

      const response = await fetch(`${SERVER_URL}/api/stats`, {
        signal: controller.signal,
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const statsData: RadioStats = await response.json();
      
      console.log('📊 Stats recibidas:', statsData);
      
      setStats(statsData);
      setConnectionError(false);
      
      // Log para debug
      if (statsData.is_streaming !== stats.is_streaming) {
        console.log(`🔄 Estado de streaming cambió: ${stats.is_streaming} -> ${statsData.is_streaming}`);
      }
      
    } catch (error) {
      console.error('❌ Error obteniendo stats:', error);
      if (isPlaying) {
        setConnectionError(true);
      }
    }
  };

  // FIXED: Mejorar addListener con mejor manejo de errores
  const addListener = async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(`${SERVER_URL}/api/listeners/add`, { 
        method: 'POST',
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const result = await response.json();
        if (result.listener_id) {
          setMyListenerId(result.listener_id);
          console.log('✅ Listener añadido:', result.listener_id);
        }
      }
    } catch (error) {
      console.error('❌ Error añadiendo listener:', error);
    }
  };

  // FIXED: Mejorar removeListener
  const removeListener = async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(`${SERVER_URL}/api/listeners/remove`, { 
        method: 'POST',
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        setMyListenerId(null);
        console.log('✅ Listener removido');
      }
    } catch (error) {
      console.error('❌ Error removiendo listener:', error);
    }
  };

  // FIXED: WebSocket connection mejorada
  const connectWebSocket = () => {
    try {
      // Cerrar conexión existente si existe
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }

      console.log('🔌 Conectando WebSocket a:', WS_URL);
      
      const ws = new WebSocket(WS_URL);
      
      ws.onopen = () => {
        console.log('✅ WebSocket conectado');
        wsRef.current = ws;
        setConnectionError(false);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'stats' && data.stats) {
            console.log('📊 Stats desde WebSocket:', data.stats);
            setStats(data.stats);
          }
        } catch (error) {
          console.error('❌ Error procesando mensaje WebSocket:', error);
        }
      };

      ws.onclose = (event) => {
        console.log('🔌 WebSocket cerrado:', event.code, event.reason);
        wsRef.current = null;
        
        // Reconectar después de 10 segundos si no fue cierre intencional
        if (event.code !== 1000) {
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('🔄 Reintentando conexión WebSocket...');
            connectWebSocket();
          }, 10000);
        }
      };

      ws.onerror = (error) => {
        console.error('❌ Error WebSocket:', error);
        setConnectionError(true);
      };

    } catch (error) {
      console.error('❌ Error creando WebSocket:', error);
      // Fallback a polling HTTP
      startStatsUpdates();
    }
  };

  const startStatsUpdates = () => {
    updateStats(); // Actualizar inmediatamente
    
    // FIXED: Solo crear interval si no existe
    if (!statsIntervalRef.current) {
      statsIntervalRef.current = setInterval(updateStats, 5000); // Cada 5 segundos
    }
  };

  const stopStatsUpdates = () => {
    if (statsIntervalRef.current) {
      clearInterval(statsIntervalRef.current);
      statsIntervalRef.current = null;
    }
  };

  const startTimeTracking = () => {
    if (!timeUpdateIntervalRef.current) {
      timeUpdateIntervalRef.current = setInterval(() => {
        if (audioRef.current && isPlaying) {
          const current = audioRef.current.currentTime;
          setCurrentTime(current);
          
          if (audioRef.current.seekable && audioRef.current.seekable.length > 0) {
            const end = audioRef.current.seekable.end(0);
            setBufferEnd(end);
            setDuration(end);
          } else if (audioRef.current.duration) {
            setDuration(audioRef.current.duration);
            setBufferEnd(audioRef.current.duration);
          }
          
          checkIfLive();
          
          const delay = bufferEnd - current;
          if (delay > 60 && isPlaying) {
            console.log(`⏰ Stream retrasado por ${delay.toFixed(1)}s`);
          }
        }
      }, 1000);
    }
  };

  const stopTimeTracking = () => {
    if (timeUpdateIntervalRef.current) {
      clearInterval(timeUpdateIntervalRef.current);
      timeUpdateIntervalRef.current = null;
    }
  };

  // FIXED: Mejorar el useEffect inicial
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume / 100;
      
      const audio = audioRef.current;
      
      const handleProgress = () => {
        if (audio.seekable && audio.seekable.length > 0) {
          const end = audio.seekable.end(0);
          setBufferEnd(end);
          setDuration(end);
        }
      };

      const handleTimeUpdate = () => {
        setCurrentTime(audio.currentTime);
        checkIfLive();
      };

      const handleError = (e: Event) => {
        console.error('❌ Error de audio:', e);
        setConnectionError(true);
        setIsLoading(false);
        
        // Reintentar automáticamente si estaba reproduciendo
        if (isPlaying) {
          console.log('🔄 Reintentando conexión en 5 segundos...');
          setTimeout(() => {
            if (isPlaying) {
              playRadio();
            }
          }, 5000);
        }
      };

      const handleCanPlay = () => {
        console.log('✅ Audio listo para reproducir');
        setConnectionError(false);
        setIsLoading(false);
        
        setTimeout(() => {
          forceGoToLive();
        }, 100);
      };

      const handleLoadStart = () => {
        console.log('🔄 Iniciando carga de audio...');
        setIsLoading(true);
      };

      audio.addEventListener('progress', handleProgress);
      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('error', handleError);
      audio.addEventListener('canplay', handleCanPlay);
      audio.addEventListener('loadstart', handleLoadStart);

      return () => {
        audio.removeEventListener('progress', handleProgress);
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('error', handleError);
        audio.removeEventListener('canplay', handleCanPlay);
        audio.removeEventListener('loadstart', handleLoadStart);
      };
    }
  }, []);

  // FIXED: useEffect de inicialización mejorado
  useEffect(() => {
    console.log('🚀 Inicializando Radio El Cristo...');
    
    // Restaurar volumen guardado
    const savedVolume = localStorage.getItem('radioVolume');
    if (savedVolume) {
      const vol = parseInt(savedVolume);
      setVolume(vol);
      if (audioRef.current) {
        audioRef.current.volume = vol / 100;
      }
    }

    // Verificar estado inicial del servidor
    updateStats();

    // Intentar conectar WebSocket
    connectWebSocket();

    // Cleanup function
    return () => {
      console.log('🧹 Limpiando componente Radio...');
      
      stopStatsUpdates();
      stopTimeTracking();
      
      // Cerrar WebSocket
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      
      // Cancelar timeout de reconexión
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      
      // Remover listener del servidor
      if (myListenerId) {
        removeListener();
      }
    };
  }, []);

  const delay = bufferEnd - currentTime;
  const isDelayed = delay > 15; 

  return (
    <>
      <div className="breadcrumb-wrapper hero-1 bg-cover">
        <div className="bottom-shape">
          <img src={images.head} />
        </div>
        <div className="line-shape">
          <img src={images.line} alt="shape-img" />
        </div>
        <div className="plane-shape float-bob-y">
          <img src={images.plane} alt="shape-img" />
        </div>
        <div className="doll-shape float-bob-x">
          <img src={images.doll} alt="shape-img" />
        </div>
        <div className="parasuit-shape float-bob-y">
          <img src={images.parasuit} alt="shape-img" />
        </div>
        <div className="frame-shape">
          <img src={images.frame} alt="shape-img" />
        </div>
        <div className="bee-shape float-bob-x">
          <img src={images.bee} alt="shape-img" />
        </div>
        <div className="container">
          <div className="nosotros">
            <h1 className="wow fadeInUp" data-wow-delay=".3s">{title}</h1>
          </div>
        </div>
      </div>

      <section id="radio" className="radio-player-section" style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        padding: '60px 20px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: '-50%',
          left: '-50%',
          width: '200%',
          height: '200%',
          animation: 'rotate 20s linear infinite',
          pointerEvents: 'none'
        }}></div>

        <div className="container">
          <div className="radio-container" style={{
            background: 'rgb(2, 3, 65)',
            backdropFilter: 'blur(20px)',
            borderRadius: '25px',
            padding: '50px',
            border: '1px solid rgba(255,255,255,0.2)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            textAlign: 'center',
            maxWidth: '600px',
            margin: '0 auto',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{ position: 'relative', zIndex: 1 }}>
              {/* Logo y título */}
              <div className="mb-4">
                <div style={{ 
                  fontSize: '4em', 
                  marginBottom: '20px',
                  animation: 'pulse 3s infinite'
                }}>📻</div>
                <h1 style={{
                  fontSize: '2.5em',
                  fontWeight: 'bold',
                  marginBottom: '10px',
                  textShadow: '2px 2px 4px rgb(151, 4, 4)',
                  color: 'white'
                }}>Radio El Cristo</h1>
                <p style={{
                  fontSize: '1.2em',
                  opacity: 0.9,
                  marginBottom: '30px'
                }}>Transmisión en Vivo</p>
              </div>

              {/* Estado de conexión mejorado */}
              <div className="mb-4">
                <div 
                  className={`status ${stats.is_streaming ? 'live' : 'offline'}`}
                  style={{
                    fontSize: '1.3em',
                    margin: '20px 0',
                    padding: '15px',
                    borderRadius: '15px',
                    background: stats.is_streaming 
                      ? 'rgba(76, 175, 80, 0.3)' 
                      : 'rgba(244, 67, 54, 0.3)',
                    border: stats.is_streaming 
                      ? '2px solid #4CAF50' 
                      : '2px solid #f44336',
                    transition: 'all 0.3s ease',
                    ...(stats.is_streaming && {
                      animation: 'glow 2s infinite alternate',
                      boxShadow: '0 0 20px rgba(76, 175, 80, 0.5)'
                    })
                  }}
                >
                  {isLoading ? '🔄 Conectando...' : 
                   stats.is_streaming ? '🟢 En Vivo' : '🔴 Fuera del aire'}
                </div>

                {/* Información adicional del servidor */}
                {stats.is_rtmp_connected !== undefined && (
                  <div style={{
                    fontSize: '0.9em',
                    opacity: 0.8,
                    marginBottom: '10px'
                  }}>
                    📡 RTMP: {stats.is_rtmp_connected ? '✅ Conectado' : '❌ Desconectado'}
                  </div>
                )}

                {connectionError && (
                  <div style={{
                    background: 'rgba(244, 67, 54, 0.3)',
                    border: '1px solid #f44336',
                    borderRadius: '10px',
                    padding: '15px',
                    margin: '15px 0',
                    animation: 'shake 0.5s'
                  }}>
                    ⚠️ Problema de conexión. Verificando...
                  </div>
                )}

                {/* Indicador de retraso */}
                {isPlaying && isDelayed && (
                  <div style={{
                    background: 'rgba(255, 193, 7, 0.3)',
                    border: '1px solid #FFC107',
                    borderRadius: '10px',
                    padding: '10px',
                    margin: '10px 0',
                    fontSize: '0.9em'
                  }}>
                    ⏰ Retraso: {formatTime(delay)} - 
                    <button
                      onClick={fastForward}
                      style={{
                        background: 'transparent',
                        border: '1px solid white',
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: '5px',
                        marginLeft: '5px',
                        cursor: 'pointer',
                        fontSize: '0.8em'
                      }}
                    >
                      ⏩ Avanzar
                    </button>
                  </div>
                )}
              </div>

              {/* Controles de reproducción */}
              <div className="mb-4 d-flex justify-content-center align-items-center gap-3">
                {/* Botón de avance rápido */}
                {isPlaying && !isLive && (
                  <button
                    onClick={fastForward}
                    style={{
                      background: 'linear-gradient(45deg, #FF9800, #F57C00)',
                      border: 'none',
                      borderRadius: '50%',
                      width: '50px',
                      height: '50px',
                      fontSize: '1.2em',
                      color: 'white',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 5px 15px rgba(0,0,0,0.3)'
                    }}
                  >
                    ⏩
                  </button>
                )}

                {/* Botón principal de play/pause */}
                <button
                  onClick={togglePlay}
                  disabled={isLoading}
                  className={`play-button ${isPlaying ? 'playing' : ''}`}
                  style={{
                    background: isPlaying 
                      ? 'linear-gradient(45deg, #f44336, #d32f2f)'
                      : 'linear-gradient(45deg, #4CAF50, #45a049)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '80px',
                    height: '80px',
                    fontSize: '2em',
                    color: 'white',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
                    opacity: isLoading ? 0.7 : 1,
                    ...(isPlaying && {
                      animation: 'playingPulse 1.5s infinite'
                    })
                  }}
                  onMouseEnter={(e) => {
                    if (!isLoading) {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow = '0 15px 35px rgba(0,0,0,0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isLoading) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,0,0,0.3)';
                    }
                  }}
                >
                  {isLoading ? '⏳' : isPlaying ? '⏸️' : '▶️'}
                </button>

                {/* Botón ir al vivo */}
                {isPlaying && !isLive && (
                  <button
                    onClick={goToLive}
                    style={{
                      background: 'linear-gradient(45deg, #f44336, #d32f2f)',
                      border: 'none',
                      borderRadius: '50%',
                      width: '50px',
                      height: '50px',
                      fontSize: '1.2em',
                      color: 'white',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 5px 15px rgba(0,0,0,0.3)'
                    }}
                  >
                    🔴
                  </button>
                )}
              </div>

              {/* Visualizador de audio */}
              {isPlaying && (
                <div className="audio-visualizer d-flex justify-content-center align-items-center mb-4" style={{ height: '40px' }}>
                  {[...Array(5)].map((_, i) => (
                    <div
                      key={i}
                      className="bar"
                      style={{
                        width: '4px',
                        height: '20px',
                        background: 'white',
                        margin: '0 2px',
                        borderRadius: '2px',
                        animation: `visualizer 1s infinite ease-in-out ${i * 0.1}s`
                      }}
                    ></div>
                  ))}
                </div>
              )}

              {/* Control de volumen */}
              <div className="volume-control d-flex align-items-center justify-content-center mb-4" style={{ gap: '15px' }}>
                <span>🔈</span>
                <input
                  type="range"
                  className="volume-slider"
                  min="0"
                  max="100"
                  value={volume}
                  onChange={(e) => handleVolumeChange(Number(e.target.value))}
                  style={{
                    width: '60%',
                    height: '8px',
                    borderRadius: '10px',
                    background: 'rgba(255,255,255,0.3)',
                    outline: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                />
                <span>🔊</span>
              </div>

              {/* Control de tiempo */}
              {isPlaying && (
                <div className="time-control mb-4">
                  <div className="d-flex align-items-center justify-content-between mb-2" style={{ fontSize: '0.9em', opacity: 0.8 }}>
                    <span>{formatTime(currentTime)}</span>
                    <div className="d-flex align-items-center gap-2">
                      {isLive ? (
                        <span style={{ 
                          background: 'rgba(76, 175, 80, 0.3)',
                          border: '1px solid #4CAF50',
                          padding: '2px 8px',
                          borderRadius: '8px',
                          fontSize: '0.8em',
                          animation: 'glow 2s infinite alternate'
                        }}>
                          🔴 EN VIVO
                        </span>
                      ) : (
                        <span style={{ 
                          background: 'rgba(255, 193, 7, 0.3)',
                          border: '1px solid #FFC107',
                          padding: '2px 8px',
                          borderRadius: '8px',
                          fontSize: '0.8em'
                        }}>
                          📼 DIFERIDO (-{formatTime(delay)})
                        </span>
                      )}
                    </div>
                    <span>{bufferEnd > 0 ? formatTime(bufferEnd) : '--:--'}</span>
                  </div>
                  <div className="position-relative">
                    <input
                      type="range"
                      className="time-slider w-100"
                      min="0"
                      max={bufferEnd || 100}
                      value={currentTime}
                      onChange={(e) => handleTimeChange(Number(e.target.value))}
                      disabled={false}
                      style={{
                        height: '6px',
                        borderRadius: '10px',
                        background: 'rgba(255,255,255,0.3)',
                        outline: 'none',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                    />
                    {/* Indicador visual del buffer */}
                    <div
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '0',
                        transform: 'translateY(-50%)',
                        height: '6px',
                        background: isLive ? 'rgba(76, 175, 80, 0.5)' : 'rgba(255, 193, 7, 0.5)',
                        borderRadius: '10px',
                        width: `${bufferEnd > 0 ? ((currentTime / bufferEnd) * 100) : 0}%`,
                        pointerEvents: 'none',
                        transition: 'all 0.3s ease'
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Panel de información mejorado */}
              <div className="info-panel" style={{
                background: 'rgb(146, 4, 4)',
                borderRadius: '15px',
                padding: '20px',
                margin: '20px 0'
              }}>
                <div className="now-playing mb-3" style={{
                  fontSize: '1.1em',
                  fontWeight: 'bold'
                }}>
                  🎵 {stats.stream_info?.station_name || 'Colegio El Cristo Radio en Vivo'}
                </div>

                <div className="stats d-flex justify-content-center gap-4">
                  <div className="stat text-center">
                    <span className="stat-value d-block" style={{
                      fontSize: '1.5em',
                      fontWeight: 'bold'
                    }}>{stats.listeners_count || 0}</span>
                    <span className="stat-label" style={{
                      fontSize: '0.8em',
                      opacity: 0.7
                    }}>Oyentes</span>
                  </div>
                  
                  <div className="stat text-center">
                    <span className="stat-value d-block" style={{
                      fontSize: '1.5em',
                      fontWeight: 'bold'
                    }}>{stats.stream_info?.bitrate || '128k'}</span>
                    <span className="stat-label" style={{
                      fontSize: '0.8em',
                      opacity: 0.7
                    }}>Calidad</span>
                  </div>
                  
                  <div className="stat text-center">
                    <span className="stat-value d-block" style={{
                      fontSize: '1.5em',
                      fontWeight: 'bold'
                    }}>📡</span>
                    <span className="stat-label" style={{
                      fontSize: '0.8em',
                      opacity: 0.7
                    }}>Digital</span>
                  </div>
                </div>

                {/* Debug info (solo en desarrollo) */}
                {process.env.NODE_ENV === 'development' && (
                  <div style={{
                    marginTop: '15px',
                    padding: '10px',
                    background: 'rgba(0,0,0,0.2)',
                    borderRadius: '8px',
                    fontSize: '0.8em',
                    textAlign: 'left'
                  }}>
                    <div>🔧 Debug:</div>
                    <div>• Streaming: {stats.is_streaming ? '✅' : '❌'}</div>
                    <div>• RTMP: {stats.is_rtmp_connected ? '✅' : '❌'}</div>
                    <div>• WebSocket: {wsRef.current?.readyState === 1 ? '✅' : '❌'}</div>
                    <div>• Player ID: {myListenerId ? '✅' : '❌'}</div>
                  </div>
                )}
              </div>

              <div className="footer" style={{
                fontSize: '0.8em',
                opacity: 0.6,
                marginTop: '30px'
              }}>
                Radio Powered by Creatimus Linuxcorp © 2025 | Transmisión en vivo
              </div>
            </div>

            <audio
              ref={audioRef}
              preload="none"
            >
              Tu navegador no soporta audio HTML5.
            </audio>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes rotate {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 0.8; }
          50% { transform: scale(1.1); opacity: 1; }
        }

        @keyframes visualizer {
          0%, 100% { height: 20px; opacity: 0.5; }
          50% { height: 40px; opacity: 1; }
        }

        @keyframes glow {
          from { box-shadow: 0 0 20px rgba(76, 175, 80, 0.5); }
          to { box-shadow: 0 0 30px rgba(76, 175, 80, 0.8); }
        }

        @keyframes playingPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }

        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }

        .volume-slider::-webkit-slider-thumb {
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: white;
          cursor: pointer;
          box-shadow: 0 2px 10px rgba(0,0,0,0.3);
          transition: all 0.3s ease;
        }

        .volume-slider::-webkit-slider-thumb:hover {
          transform: scale(1.2);
        }

        .volume-slider::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: white;
          cursor: pointer;
          border: none;
          box-shadow: 0 2px 10px rgba(0,0,0,0.3);
        }

        .time-slider::-webkit-slider-thumb {
          appearance: none;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: white;
          cursor: pointer;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          transition: all 0.3s ease;
        }

        .time-slider::-webkit-slider-thumb:hover {
          transform: scale(1.2);
        }

        .time-slider::-moz-range-thumb {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: white;
          cursor: pointer;
          border: none;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        }

        .time-slider:disabled::-webkit-slider-thumb {
          background: #f44336;
          cursor: not-allowed;
        }

        .time-slider:disabled::-moz-range-thumb {
          background: #f44336;
          cursor: not-allowed;
        }

        @media (max-width: 600px) {
          .radio-container {
            padding: 30px 20px !important;
            margin: 10px !important;
          }

          .radio-container h1 {
            font-size: 2em !important;
          }

          .play-button {
            width: 70px !important;
            height: 70px !important;
            font-size: 1.8em !important;
          }

          .mb-4 .btn {
            width: 45px !important;
            height: 45px !important;
            font-size: 1em !important;
          }

          .stats {
            flex-direction: column !important;
            gap: 10px !important;
          }
        }
      `}</style>
    </>
  );
};

export default KidsaRadio;